import { FC, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import AppLayout from '../../components/layout/app-layout/app-layout';
import styles from './page-get-started.module.scss';
import { useTranslation } from 'react-i18next';
import TaskCard from '../../components/shared/TaskCard/TaskCard';
import OpportunityCard from '../../components/shared/OpportunityCard/OpportunityCard';
import { ReactComponent as CompanyIcon } from '../../assets/svg/corporate_fare.svg';
import { ReactComponent as TeamIcon } from '../../assets/svg/group.svg';
import {ReactComponent as thunderIcon} from '../../assets/svg/thunderIcon.svg';
import { ReactComponent as SuccessSignUpIcon } from '../../assets/svg/successSignUpIcon.svg';
import { ReactComponent as eyeIcon } from '../../assets/svg/openedEyeIcon.svg';
import { ReactComponent as InfoIcon } from '../../assets/svg/info.svg';
import InfoBox from '../../components/shared/InfoBox/InfoBox';
import { ServiceUser } from '../../services/service-user';
import appState from '../../state/AppStateContainer';
import Modal from '../../components/shared/Modal/Modal';
import Field from '../../components/shared/Field/Field';
import serviceSales from '../../services/service-sales';
import { serviceOrganization } from '../../services/service-organization';
import { NotificationManager } from 'react-notifications';
import { isValidEmail } from '../../util';
import { AdModal } from '../page-marketing/ad-modal';
import { AdApplication } from '../page-marketing/page-marketing';
import { AdApplicationType } from '../../util/appEnum';
import { serviceAreas } from '../../services/service-areas';
import { serviceAdApplication } from '../../services/service-ad-application';
import moment from 'moment';
import { canManageOrgSettings } from '../../util/settingsAccess';
const PageGetStarted: FC = () => {
  const [t] = useTranslation('getStarted');
  const [isTeamComplete, setIsTeamComplete] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [startSubscriptionShow, setStartSubscriptionShow] = useState(false);
  const [selectedSubscriptionType, setSelectedSubscriptionType] = useState<'published-quotations' | 'recommended'>('published-quotations');
  const [errorMessages, setErrorMessages] = useState({
    invoiceEmail: '',
    invoiceRef: '',
  });
  const [isApplicationModalVisible, setIsApplicationModalVisible] = useState<boolean>(false);
  const [adApplication, setAdApplication] = useState<AdApplication>();
  const [areas, setAreas] = useState<{id: string, label: string, value: string}[]>([]);
  const isProfileComplete = appState.hasSupplierProfileBeenUpdated();
  const history = useHistory();

  useEffect(() => {
    if (!appState.isSalesActive()) {
      const orgSlug = appState.getCurrentOrgSlug();
      history.push(`/${orgSlug}/dashboard`);
    }
  }, [history]);

  const invoiceEmail: string = appState.getInvoiceEmail();
  const invoiceRef: string = appState.getInvoiceRef();

  useEffect(() => {
    const checkTeamMembers = async () => {
      try {
        const users = await ServiceUser.getTasksUsers();
        const teamComplete = users.length >= 2;
        setIsTeamComplete(teamComplete);
        
        if (isProfileComplete && teamComplete) {
          const currentPath = window.location.pathname;
          const newPath = currentPath.replace('/get-started', '/requests');
          history.push(newPath);
        }
      } catch (error) {
        console.error('Error fetching team members:', error);
        setIsTeamComplete(false);
      }
    };

    const fetchSubscriptions = async () => {
      try {
        await serviceSales.fetchPublishedQuotationsSubscription();
        await serviceSales.fetchRecommendedSubscription();
      } catch (error) {
        console.error('Error fetching subscriptions:', error);
      }
    };

    checkTeamMembers();
    fetchSubscriptions();
    
    // Fetch areas for AdModal
    const fetchAreas = async () => {
      try {
        const areasData: {_id: string, name: string}[] = await serviceAreas.fetchSuperAdminAreas();
        const areas: {id: string, label: string, value: string}[] = areasData.map(item => ({ id: item._id, label: item.name, value: item.name }));
        setAreas(areas);
      } catch (error) {
        console.error('Error fetching areas:', error);
      }
    };
    
    fetchAreas();
  }, [isProfileComplete, history]);

  // Calculate completion percentage
  const completedTasks = [isProfileComplete, isTeamComplete].filter(Boolean).length;
  const totalTasks = 2;
  const completionPercentage = Math.round((completedTasks / totalTasks) * 100);

  const onSubscribe = async () => {
    setSubmitting(true);
    const fields = {
      invoiceEmail,
      invoiceRef,
    };

    const errorMessages = Object.entries(fields).reduce((total, [key, value]) => {
      if (!value) {
        total[key] = t('validation.required', 'Detta fält är obligatoriskt');
      }
      if (key === 'invoiceEmail' && !isValidEmail(value)) {
        total[key] = t('validation.email', 'Felaktig e-postadress');
      }
      return total;
    }, {} as {[key: string]: string});

    if (Object.keys(errorMessages).length > 0) {
      setErrorMessages(errorMessages as {invoiceEmail: string, invoiceRef: string});
      setSubmitting(false);
      return;
    }
    const responseInvoiceEmail = await serviceOrganization.updateField("invoiceEmail", invoiceEmail);
    if (responseInvoiceEmail.invoiceEmail) {
      errorMessages.invoiceEmail = responseInvoiceEmail.invoiceEmail;
    }
    const responseInvoiceRef = await serviceOrganization.updateField("invoiceRef", invoiceRef);
    if (responseInvoiceEmail.invoiceRef) {
      errorMessages.invoiceRef = responseInvoiceRef.invoiceRef;
    }
    if (Object.keys(errorMessages).length > 0) {
      setErrorMessages(errorMessages as {invoiceEmail: string, invoiceRef: string});
      setSubmitting(false);
      return;
    }

    await appState.fetchOrganizations();

    // Call the appropriate subscription service based on selected type
    if (selectedSubscriptionType === 'published-quotations') {
      await serviceSales.orderPublishedQuotationsSubscription();
    } else {
      await serviceSales.orderRecommendedSubscription();
    }
    
    setErrorMessages({
      invoiceEmail: '',
      invoiceRef: '',
    });
    setStartSubscriptionShow(false);
    setSubmitting(false);
    NotificationManager.success(
      t('notification.success.ordered', 'Prenumerationen kommer förnyas automatiskt ett år i taget.'),
      t('notification.success.title', 'Sparat')
    );
  };

  const submitRecommendedSupplierAd = async () => {
    try {
      setSubmitting(true);
      await serviceAdApplication.createRecommendedAdApplication({
        ad: {
          startDate: adApplication!.startDate!,
          regions: adApplication!.regions?.map(r => r?.value).filter((value): value is string => Boolean(value)) || [],
          areas: adApplication!.areas?.map(a => a.id) || [],
        },
        invoiceInterval: adApplication!.invoiceInterval!,
        invoiceEmail: adApplication!.invoiceEmail!,
        invoiceRef: adApplication!.invoiceRef!,
      });
      setIsApplicationModalVisible(false);
      NotificationManager.success(
        t('notification.success.adApplication', 'Din ansökan har skickats in.'),
        t('notification.success.title', 'Sparat')
      );
    } catch (error) {
      console.error('Error submitting recommended supplier ad:', error);
      NotificationManager.error(
        t('notification.error.adApplication', 'Ett fel uppstod vid ansökan.'),
        t('notification.error.title', 'Fel')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const submitFeaturedSupplierAd = async () => {
    try {
      setSubmitting(true);
      await serviceAdApplication.createFeaturedAdApplication({
        featuredInfo: {
          url: adApplication!.url!,
          image: adApplication!.image as File,
          title: adApplication!.title!,
          description: adApplication!.description!,
          startDate: adApplication!.startDate!,
        },
        invoiceInterval: adApplication!.invoiceInterval!,
        invoiceEmail: adApplication!.invoiceEmail!,
        invoiceRef: adApplication!.invoiceRef!,
      });
      setIsApplicationModalVisible(false);
      NotificationManager.success(
        t('notification.success.adApplication', 'Din ansökan har skickats in.'),
        t('notification.success.title', 'Sparat')
      );
    } catch (error) {
      console.error('Error submitting featured supplier ad:', error);
      NotificationManager.error(
        t('notification.error.adApplication', 'Ett fel uppstod vid ansökan.'),
        t('notification.error.title', 'Fel')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const tasks = [
    {
      id: 'company-presentation',
      title: t('tasks.companyPresentation.title', 'Slutför din företagspresentation'),
      description: t('tasks.companyPresentation.description', 'Fyll i din presentation så att inköpare vet vem du är och vad du erbjuder.'),
      isCompleted: isProfileComplete,
      buttonText: t('tasks.companyPresentation.button', 'Redigera presentation'),
      icon: isProfileComplete ? SuccessSignUpIcon : CompanyIcon,
      redirectTo: '/supplier-presentation'
    },
    {
      id: 'invite-team',
      title: t('tasks.inviteTeam.title', 'Bjud in ditt team'),
      description: t('tasks.inviteTeam.description', 'Lägg till kollegor som kan hjälpa till att hantera din företagsprofil eller vara kontaktpersoner för inköpare.'),
      isCompleted: isTeamComplete,
      buttonText: t('tasks.inviteTeam.button', 'Bjud in team'),
      icon: isTeamComplete ? SuccessSignUpIcon : TeamIcon,
      redirectTo: '/settings/users',
      note: canManageOrgSettings(appState.getUserRole())
        ? undefined
        : t('tasks.inviteTeam.askAdmin', 'Be en administratör att bjuda in kollegor')
    }
  ];

  const opportunities = [
    {
      id: 'published-requests',
      title: t('opportunities.publishedRequests.title', 'Publicerade förfrågningar'),
      description: t('opportunities.publishedRequests.description', 'Accurators marknadsplats med 10-20% av alla upphandlingar där inköpare aktivt söker nya leverantörer.'),
      buttonText: t('opportunities.publishedRequests.buttonText', 'Starta prenumeration'),
      icon: eyeIcon,
      onButtonClick: () => {
        setSelectedSubscriptionType('published-quotations');
        setStartSubscriptionShow(true);
      },
      redirectTo: '/published-requests',
      features: [
        {
          icon: InfoIcon,
          title: t('opportunities.publishedRequests.features.includedInPrio.title', 'Ingår i Prio. Kan köpas separat'),
          description: '',
          isInfo: true
        },
        {
          icon: SuccessSignUpIcon,
          title: t('opportunities.publishedRequests.features.findJobs.title', 'Hitta nya jobb'),
          description: t('opportunities.publishedRequests.features.findJobs.description', 'Sök, filtrera och bevaka publicerade förfrågningar som matchar er profil.')
        },
        {
          icon: SuccessSignUpIcon,
          title: t('opportunities.publishedRequests.features.monitoring.title', 'Bevakning'),
          description: t('opportunities.publishedRequests.features.monitoring.description', 'Få notiser när nya jobb dyker upp inom era kategorier och län')
        },
        {
          icon: SuccessSignUpIcon,
          title: t('opportunities.publishedRequests.features.smoothHandling.title', 'Smidig hantering'),
          description: t('opportunities.publishedRequests.features.smoothHandling.description', 'Läs, ställ frågor och lämna anbud direkt via Accurator.')
        }
      ],
      price: t('opportunities.publishedRequests.price', 'Från. 799 kr/månad'),
      secondaryButtonText: t('opportunities.publishedRequests.secondaryButtonText', 'Läs mer om tjänsten')
    },
    {
      id: 'accurator-prio',
      title: t('opportunities.accuratorPrio.title', 'Accurator Prio'),
      description: t('opportunities.accuratorPrio.description', 'Prio samlar alla era förfrågningar på ett ställe och lyfter fram er som rekommenderad leverantör för inköpare'),
      buttonText: t('opportunities.accuratorPrio.buttonText', 'Starta prenumeration'),
      icon: thunderIcon,
      onButtonClick: () => {
        setIsApplicationModalVisible(true);
        setAdApplication({
          errors: {},
          startDate: moment().toDate(),
          regions: [],
          areas: [],
          applicationType: AdApplicationType.RECOMMENDED,
          invoiceInterval: undefined,
          invoiceEmail: appState.getInvoiceEmail(),
          invoiceRef: appState.getInvoiceRef(),
        });
      },
      redirectTo: '/marketing',
      features: [
        {
          icon: InfoIcon,
          title: t('opportunities.accuratorPrio.features.allIncluded.title', 'Allt i Publicerade förfrågningar + Kontroll + Rekommenderad leverantör.'),
          description: '',
          isInfo: true
        },
        {
          icon: SuccessSignUpIcon,
          title: t('opportunities.accuratorPrio.features.control.title', 'Kontroll'),
          description: t('opportunities.accuratorPrio.features.control.description', 'Se alla förfrågningar som skickats till ert företag, oavsett vem i teamet som fick förfrågan eller är inloggad.')
        },
        {
          icon: SuccessSignUpIcon,
          title: t('opportunities.accuratorPrio.features.recommendedSupplier.title', 'Rekommenderad leverantör'),
          description: t('opportunities.accuratorPrio.features.recommendedSupplier.description', 'Vi marknadsför er mot inköpare i Accurator och nudge:ar dem i skarpt läge när ett köp matchar er profil.')
        },
        {
          icon: SuccessSignUpIcon,
          title: t('opportunities.accuratorPrio.features.resultsDashboard.title', 'Resultatdashboard'),
          description: t('opportunities.accuratorPrio.features.resultsDashboard.description', 'Följ effekten av Prio - fler förfrågningar, vunna affärer, nya inköpskunder som lagt till er i huvudregistret.')
        }
      ],
      price: t('opportunities.accuratorPrio.price', 'Från. 2395 kr/månad'),
      secondaryButtonText: t('opportunities.accuratorPrio.secondaryButtonText', 'Läs mer om tjänsten')
    }
  ];

  return (
    <AppLayout title={t('welcome.title', 'Välkommen till Accurator')}>
      <div className={styles.pageGetStarted}>
        {/* Header Section */}
        <div className={styles.header}>
          <InfoBox
            title={t('welcome.subtitle', 'Snart klar!')}
            description={t('welcome.description', 'Slutför stegen nedan för att låsa upp full åtkomst och öka din synlighet.')}
          />
        </div>

        {/* Progress Section */}
        <div className={styles.progressSection}>
          <div className={styles.progressHeader}>
            <span className={styles.progressLabel}>
              {t('progress.status', 'Status')}
            </span>
            <span className={styles.progressPercentage}>
              {completionPercentage}% {t('progress.completed', 'Slutfört')}
            </span>
          </div>
          <div className={styles.progressBar}>
            <div 
              className={styles.progressBarFill} 
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
          <div className={styles.progressDescription}>
            {t('progress.description', '2 steg - tar cirka 5 minuter att slutföra.')}
          </div>
        </div>

        {/* Tasks Section */}
        <div className={styles.tasksSection}>
          {tasks.map((task) => (
            <TaskCard
              key={task.id}
              title={task.title}
              description={task.description}
              isCompleted={task.isCompleted}
              buttonText={task.buttonText}
              note={task.note}
              icon={task.icon}
              redirectTo={task.redirectTo}
            />
          ))}
        </div>

        {/* Opportunities Section */}
        <div className={styles.opportunitiesSection}>
          <div className={styles.opportunitiesTitle}>
            {t('opportunities.title', 'Utforska fler möjligheter')}
          </div>
          <div className={styles.opportunitiesDescription}>
            {t('opportunities.description', 'Två sätt att hitta och vinna fler jobb. Välj Accurator Prio för full kontroll och extra synlighet eller starta en prenumeration på Publicerade förfrågningar för att bevaka och ta del av publicerade upphandlingar.')}
          </div>
          <div className={styles.opportunitiesGrid}>
            {opportunities.map((opportunity) => {
              const isAccuratorPrio = opportunity.id === 'accurator-prio';
              const isProfileIncomplete = !isProfileComplete;
              
              return (
                <OpportunityCard
                  key={opportunity.id}
                  title={opportunity.title}
                  description={opportunity.description}
                  buttonText={opportunity.buttonText}
                  icon={opportunity.icon}
                  redirectTo={opportunity.redirectTo}
                  onButtonClick={isAccuratorPrio && isProfileIncomplete ? () => {} : opportunity.onButtonClick}
                  features={opportunity.features}
                  price={opportunity.price}
                  secondaryButtonText={opportunity.secondaryButtonText}
                  notFill={isAccuratorPrio}
                  disabled={isAccuratorPrio && isProfileIncomplete}
                  tooltipMessage={isAccuratorPrio && isProfileIncomplete ? 
                    <span>
                      {t('tooltip.completePresentation', 'Fyll i')} <span 
                        className={styles.tooltipLink}
                        onClick={() => {
                          const orgSlug = appState.getCurrentOrgSlug();
                          history.push(`/${orgSlug}/supplier-presentation`);
                        }}
                      >
                        {t('tooltip.supplierPresentation', 'leverantörspresentationen')}
                      </span> {t('tooltip.first', 'först.')}
                    </span> : undefined}
                />
              );
            })}
          </div>
        </div>
      </div>
      <Modal
        title={t('published.subscription.heading', 'Beställ prenumeration')}
        show={startSubscriptionShow}
        setShow={setStartSubscriptionShow}
        buttonInfo={{ label: t('published.subscription.button', 'Bekräfta köp'), action: onSubscribe, loaderShow: submitting }}
      >
        <div style={{ fontSize: "14px", color: "var(--gray-700)", maxWidth: "500px" }}>
          {selectedSubscriptionType === 'published-quotations' 
            ? t('published.subscription.intro', 'Tjänsten "visa publicerade förfrågningar" är en prenumerationstjänst som betalas månadsvis och förnyas automatiskt med ett år i taget.')
            : t('recommended.subscription.intro', 'Tjänsten "Accurator Prio" är en prenumerationstjänst som betalas månadsvis och förnyas automatiskt med ett år i taget.')
          }

          <h3>{t('published.subscription.subheading', 'Beställning')}</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div>
                {selectedSubscriptionType === 'published-quotations' 
                  ? t('published.subscription.product', 'Visa publicerade förfrågningar, 12 månader')
                  : t('recommended.subscription.product', 'Accurator Prio, 12 månader')
                }
              </div>
              <div>
                {selectedSubscriptionType === 'published-quotations' 
                  ? t('published.subscription.price', '799 kr/månad')
                  : t('recommended.subscription.price', '1995 kr/månad')
                }
              </div>
            </div>
          </div>

          {(
            !appState.getCurrentOrg()?.invoiceEmail ||
            !appState.getCurrentOrg()?.invoiceRef
          ) && (
            <>
              <h3>{t('published.subscription.invoiceDetails.heading', 'Fakturauppgifter')}</h3>
              <p>{t('published.subscription.invoiceDetails.intro', 'Vi saknar era fakturauppgifter. Uppgifterna sparas och kommer användas även för framtida köp av tilläggstjänster.')}</p>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "8px" }}>
                <Field
                  label={t('published.subscription.invoiceDetails.label.email', 'E-postadress')}
                  value={invoiceEmail}
                  onChange={invoiceEmail => appState.setInvoiceEmail(invoiceEmail)}
                  errorMessage={errorMessages.invoiceEmail}
                  required
                />
                <Field
                  label={t('published.subscription.invoiceDetails.label.ref', 'Referens')}
                  value={invoiceRef}
                  onChange={invoiceRef => appState.setInvoiceRef(invoiceRef)}
                  errorMessage={errorMessages.invoiceRef}
                  required
                />
              </div>
            </>
          )}
        </div>
      </Modal>
      {adApplication && (
        <AdModal
          title={t('adModal.title', 'Ansökan')}
          show={isApplicationModalVisible}
          setShow={(value) => setIsApplicationModalVisible(value)}
          adApplication={adApplication}
          setAdApplication={setAdApplication}
          submitFeaturedSupplierAd={submitFeaturedSupplierAd}
          submitRecommendedSupplierAd={submitRecommendedSupplierAd}
          submitting={submitting}
          setSubmitting={setSubmitting}
          areas={areas}
        />
      )}
    </AppLayout>
  );
};

export default PageGetStarted;
